import React, { useCallback, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner, Tab, Tabs, TabsExpander } from '@blueprintjs/core';
import { toast } from 'sonner';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { queryPolicies } from '../../utils/queryPolicies';
import {
  BATCH_LIMIT, MAX_UPLOAD_MB, UPLOAD_ACCEPT, fetchBatch, fetchBatches, lastBatchReadMs, fetchLeadSources, uploadOriginal, uploadProblem, type BatchDetail, type BatchListRow, type BatchStatus, type SourceLocation,
} from '../LeadImport/api';
import DriveSheetPicker from '../LeadImport/DriveSheetPicker';
import { GoogleDriveError, downloadDriveSpreadsheet, type DriveSpreadsheetMeta } from '../../features/docs/drive';
import { leadImportKeys } from '../LeadImport/keys';
import ImportWizard from '../LeadImport/ImportWizard';
import BatchWorkspace from '../LeadImport/BatchWorkspace';
import AssignmentsView from '../LeadImport/AssignmentsView';
import Redistribute from '../LeadImport/Redistribute';
import HoldQueue from '../LeadImport/HoldQueue';
import LeadDrawer from '../LeadImport/LeadDrawer';
import LeadsBrowser from '../LeadImport/LeadsBrowser';
import { useVerifyLeads } from '../LeadImport/VerifyLeads';
import NewAssignment, { seedFrom, type AssignmentSeed } from '../LeadImport/NewAssignment';
import { MappingProfilesView, PurchasesView, ReportsView, VerificationView } from '../LeadImport/HubViews';
import { Card, Empty, ErrorNote, SortTh, StatusPill, fmtDay, fmtInt, fmtMoney, fmtWhen, statusLabel, useSorted } from '../LeadImport/ui';

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso)) : '-';
import '../LeadImport/LeadImport.css';
import { PickList } from '../ui/PickList';

/**
 * Lead imports: bring in vendor lead files, review them, give leads to reps.
 *
 * URL state: ?tab=<view> for the hub views, ?batch=<id> to open one import
 * and ?view=<tab> for its workspace tab, ?assign=new for a new assignment
 * across imports, ?lead=<id> for one lead.
 * An import that has not been started yet opens the setup wizard; a started
 * one opens its workspace. The page requires the Lead import permission and
 * the server checks the same permission on every call.
 */

type HubTab = 'imports' | 'leads' | 'assignments' | 'redistribute' | 'holds' | 'purchases' | 'verification' | 'reports' | 'mappings';
/** Statuses where the server is working and the page should keep refreshing. */
const WORKING: BatchStatus[] = ['Inspecting', 'Preparing', 'Committing'];

export default function ImportsPage(): React.ReactElement {
  const { hasCapability, isLoading } = useMyCapabilities();
  const [params, setParams] = useSearchParams();
  const batchId = Number(params.get('batch')) || null;
  const tab = (params.get('tab') as HubTab) || 'imports';
  const assigning = params.get('assign') === 'new';
  const lead = Number(params.get('lead')) || null;
  /** The Leads tab's view, handed to a new assignment; page state, not URL. */
  const [seed, setSeed] = useState<AssignmentSeed | null>(null);

  const openBatch = useCallback((id: number) => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete('assign'); n.delete('view'); if (id) n.set('batch', String(id)); else n.delete('batch'); return n; });
  }, [setParams]);
  const openTab = useCallback((t: HubTab) => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete('batch'); n.delete('view'); n.delete('assign'); n.delete('from_rep'); n.delete('from_import'); n.set('tab', t); return n; });
  }, [setParams]);
  const startAssignment = useCallback((from: AssignmentSeed | null) => {
    setSeed(from);
    setParams((p) => { const n = new URLSearchParams(p); n.delete('batch'); n.delete('view'); n.set('assign', 'new'); return n; });
  }, [setParams]);
  const closeAssignment = useCallback(() => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete('assign'); return n; });
  }, [setParams]);
  const setLead = useCallback((id: number | null) => {
    setParams((p) => { const n = new URLSearchParams(p); if (id) n.set('lead', String(id)); else n.delete('lead'); return n; });
  }, [setParams]);

  if (isLoading) return <section className="li-page"><Spinner size={24} /></section>;
  if (!hasCapability('lead_import')) {
    return (
      <section className="li-page">
        <Empty title="Lead imports">You need the Lead import permission to use this page. Ask an admin to add it to your group.</Empty>
      </section>
    );
  }

  return (
    <section className="li-page">
      <header className="li-page__head">
        <div>
          {batchId ? (
            <button type="button" className="li-btn li-btn--quiet li-back" onClick={() => openBatch(0)}>
              <span aria-hidden>‹</span> All imports
            </button>
          ) : assigning ? (
            <>
              <button type="button" className="li-btn li-btn--quiet li-back" onClick={closeAssignment}>
                <span aria-hidden>‹</span> {tab === 'leads' ? 'Leads' : tab === 'assignments' ? 'Assignments' : 'Lead imports'}
              </button>
              <h1>New assignment</h1>
            </>
          ) : (
            <>
              <h1>Lead imports</h1>
              <p className="li-page__sub">Bring in vendor files, give the leads to reps, and see what each source costs.</p>
            </>
          )}
        </div>
      </header>

      {batchId ? (
        <BatchRoute batchId={batchId} onOpenBatch={openBatch} onOpenLead={setLead} />
      ) : assigning ? (
        <NewAssignment seed={seed} onOpenLead={setLead} onDone={() => { setSeed(null); openTab('assignments'); }} />
      ) : (
        <>
        <HubSummary onOpenTab={openTab} />
        <Tabs id="li-hub" selectedTabId={tab} onChange={(t) => openTab(t as HubTab)} renderActiveTabPanelOnly className="li-tabs">
          <Tab id="imports" title="Imports" panel={<ImportsView onOpenBatch={openBatch} />} />
          <Tab id="leads" title="Leads" panel={<LeadsTab onOpenLead={setLead} onAssign={startAssignment} />} />
          <Tab id="assignments" title="Assignments" panel={<AssignmentsView batchId={null} onOpenBatch={openBatch} onNew={() => startAssignment(null)} />} />
          <Tab id="redistribute" title="Redistribute"
            panel={<Redistribute key={`${params.get('from_rep')}-${params.get('from_import')}`} initialRep={Number(params.get('from_rep')) || null} initialImport={Number(params.get('from_import')) || null} />} />
          <Tab id="holds" title="Needs review" panel={<HoldQueue batchId={null} onOpenLead={setLead} />} />
          <TabsExpander />
          <Tab id="purchases" title="Purchases" panel={<PurchasesView />} />
          <Tab id="verification" title="Verification" panel={<VerificationView />} />
          <Tab id="reports" title="Reports" panel={<ReportsView />} />
          <Tab id="mappings" title="Saved mappings" panel={<MappingProfilesView />} />
        </Tabs>
        </>
      )}
      <LeadDrawer leadId={lead} onClose={() => setLead(null)} onOpenLead={setLead} />
    </section>
  );
}

const DAY_MS = 86_400_000;

/** The last 30 days at a glance, from the import list the Imports tab already
 *  loads (same query key), so it costs no extra read. */
function HubSummary({ onOpenTab }: { onOpenTab: (t: HubTab) => void }) {
  const batches = useQuery({ queryKey: leadImportKeys.batches({}), queryFn: () => fetchBatches({}), ...queryPolicies.pagedData });
  if (!batches.data) return null;
  const since = Date.now() - 30 * DAY_MS;
  const recent = batches.data.filter((r) => r.status === 'Committed' && Date.parse(r.committed_at ?? r.created_at) >= since);
  const sum = (f: (r: BatchListRow) => number) => recent.reduce((t, r) => t + (f(r) || 0), 0);
  const leads = sum((r) => r.observation_count);
  const unassigned = sum((r) => r.observation_count - r.assigned);
  const uncosted = recent.filter((r) => !r.cost).length;
  return (
    <div className="li-kpis" data-testid="li-hub-summary">
      <button type="button" className="li-kpi" onClick={() => onOpenTab('imports')}>
        <span className="li-kpi__label">Imported in the last 30 days</span>
        <span className="li-kpi__value">{fmtInt(leads)}</span>
        <span className="li-kpi__sub">leads from {fmtInt(recent.length)} file{recent.length === 1 ? '' : 's'}</span>
      </button>
      <button type="button" className="li-kpi" onClick={() => onOpenTab('imports')}>
        <span className="li-kpi__label">New phones</span>
        <span className="li-kpi__value li-kpi__value--accent">{fmtInt(sum((r) => r.new_identity_count))}</span>
        <span className="li-kpi__sub">never seen in an earlier import</span>
      </button>
      <button type="button" className="li-kpi" onClick={() => onOpenTab('leads')}>
        <span className="li-kpi__label">Not with a rep yet</span>
        <span className={`li-kpi__value${unassigned > 0 ? ' li-kpi__value--warn' : ''}`}>{fmtInt(unassigned)}</span>
        <span className="li-kpi__sub">{leads ? `${Math.round((unassigned / leads) * 100)}% of those leads` : 'nothing imported lately'}</span>
      </button>
      <button type="button" className="li-kpi" onClick={() => onOpenTab('purchases')}>
        <span className="li-kpi__label">Spent</span>
        <span className="li-kpi__value li-kpi__value--good">{fmtMoney(sum((r) => Number(r.cost)))}</span>
        <span className="li-kpi__sub">{uncosted ? `${uncosted} import${uncosted === 1 ? ' has' : 's have'} no cost yet` : 'every import has a cost'}</span>
      </button>
    </div>
  );
}

const isSetup = (b: BatchDetail) => !b.accepted_at && b.status !== 'Cancelled';

/** Every lead from every finished import; the start of a cross-import assignment. */
function LeadsTab({ onOpenLead, onAssign }: { onOpenLead: (id: number) => void; onAssign: (seed: AssignmentSeed) => void }) {
  const verify = useVerifyLeads();
  return (
    <>
      <LeadsBrowser
        onOpenLead={onOpenLead}
        height="calc(100vh - 330px)"
        renderActions={(v) => (
          <>
            {v.selected.length > 0 && verify.buttons(v.selected, v.clearSelection)}
            <Link className="li-btn li-btn--quiet" to="/reports/lead_explorer">Open in Reports</Link>
            <button type="button" className="li-btn li-btn--primary" disabled={v.total === 0} onClick={() => onAssign(seedFrom(v))}>
              {v.selectedIds.length ? `Give ${fmtInt(v.selectedIds.length)} to a rep` : 'Give to a rep'}
            </button>
          </>
        )}
      />
      {verify.dialog}
    </>
  );
}

function BatchRoute({ batchId, onOpenBatch, onOpenLead }: { batchId: number; onOpenBatch: (id: number) => void; onOpenLead: (id: number) => void }) {
  const q = useQuery({
    queryKey: leadImportKeys.batch(batchId),
    queryFn: () => fetchBatch(batchId),
    ...queryPolicies.recordDetail,
    // The worker does the heavy work; poll while a job is queued or running,
    // slower for other in-progress statuses. Each wait is at least four times
    // the last read, so a large batch (verification can run for many minutes)
    // never keeps the database busy with back-to-back status reads.
    refetchInterval: (query) => {
      const b = query.state.data;
      if (!b) return false;
      const running = !!b.job && (b.job.state === 'queued' || b.job.state === 'running');
      if (!running && !WORKING.includes(b.status)) return false;
      return Math.min(60_000, Math.max(running ? 1000 : 3000, 4 * lastBatchReadMs(batchId)));
    },
  });
  if (q.isLoading) return <Spinner size={24} />;
  if (q.error) return <ErrorNote error={q.error} />;
  if (!q.data) return <Empty title="Import not found">It may have been a draft that was discarded.</Empty>;
  const b = q.data;
  if (isSetup(b)) return <ImportWizard batch={b} onOpenBatch={onOpenBatch} />;
  return <BatchWorkspace batch={b} onOpenBatch={onOpenBatch} onOpenLead={onOpenLead} />;
}

/* ── Imports list and the start panel ─────────────────── */

function phaseText(r: BatchListRow): string | null {
  if (r.stage_error && r.stage_error.class !== 'transient') return r.stage_error.message;
  if (r.job?.waiting_for_worker) return 'Waiting for the import service';
  if (r.status === 'Committing' && r.commit_total > 0) return `${Math.round((r.commit_done / r.commit_total) * 100)}% done`;
  return null;
}

function ImportsView({ onOpenBatch }: { onOpenBatch: (id: number) => void }) {
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const filter = { ...(status ? { status } : {}), ...(source ? { lead_source: Number(source) } : {}) };
  const sources = useQuery({ queryKey: leadImportKeys.sources, queryFn: fetchLeadSources, ...queryPolicies.configuration });
  const batches = useQuery({
    queryKey: leadImportKeys.batches(filter),
    queryFn: () => fetchBatches(filter),
    ...queryPolicies.pagedData,
    refetchInterval: (q) => ((q.state.data ?? []).some((r) => WORKING.includes(r.status)) ? 5000 : false),
  });
  const rows = batches.data ?? [];
  const unfinished = rows.filter((r) => !r.accepted_at && r.status !== 'Cancelled');
  const [allDrafts, setAllDrafts] = useState(false);
  const shownDrafts = allDrafts ? unfinished : unfinished.slice(0, 4);
  const started = rows.filter((r) => r.accepted_at || r.status === 'Cancelled');

  return (
    <div className="li-stack">
      <StartImport onStarted={onOpenBatch} />

      {unfinished.length > 0 && (
        <Card title={<>Not imported yet <span className="li-count">{unfinished.length}</span></>} flush
          actions={unfinished.length > 4 && (
            <button type="button" className="li-btn li-btn--quiet" onClick={() => setAllDrafts(!allDrafts)}>{allDrafts ? 'Show fewer' : `Show all ${unfinished.length}`}</button>
          )}>
          <ul className="li-drafts">
            {shownDrafts.map((d) => (
              <li key={d.id}>
                <button type="button" className="li-draft" onClick={() => onOpenBatch(d.id)}>
                  <span className="li-draft__name" title={d.file_name ?? ''}>{d.file_name}</span>
                  <span className="li-draft__meta">{d.source ?? ''}</span>
                  <span className="li-draft__status"><StatusPill status={d.status} />{phaseText(d) && <span className="li-draft__phase">{phaseText(d)}</span>}</span>
                  <span className="li-draft__meta li-draft__when">{fmtWhen(d.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Imports" flush actions={
        <div className="li-row">
          <PickList value={status} onChange={setStatus} ariaLabel="Filter by status"
            options={[{ value: '', label: 'Any status' }, ...(['Committing', 'Committed', 'Failed', 'Cancelled'] as BatchStatus[]).map((s) => ({ value: s, label: statusLabel(s) }))]} />
          <PickList value={source} onChange={setSource} ariaLabel="Filter by source"
            options={[{ value: '', label: 'Any source' }, ...(sources.data ?? []).map((s) => ({ value: String(s.id), label: s.source }))]} />
        </div>
      }>
        <ErrorNote error={batches.error} />
        {batches.isLoading ? <div className="li-card__body"><Spinner size={20} /></div> : started.length === 0 ? (
          <Empty title="No imports yet">Upload a vendor file above. You will check the columns and see a preview before anything is imported.</Empty>
        ) : (
          <BatchTable rows={started} onOpenBatch={onOpenBatch} capped={rows.length >= BATCH_LIMIT} />
        )}
      </Card>
    </div>
  );
}

type BatchSort = 'file' | 'source' | 'batch_date' | 'status' | 'leads' | 'new' | 'assigned' | 'cost' | 'uploaded';
const leadsOf = (r: BatchListRow) => (r.status === 'Committed' ? r.observation_count : r.preview_observations);
const sortValue = (r: BatchListRow, k: BatchSort): string | number | null => {
  switch (k) {
    case 'file': return r.file_name;
    case 'source': return r.source ? `${r.source} ${r.sub_lead_source ?? ''}` : null;
    case 'batch_date': return r.batch_date;
    case 'status': return statusLabel(r.status);
    case 'leads': return leadsOf(r);
    case 'new': return r.status === 'Committed' ? r.new_identity_count : null;
    case 'assigned': return r.status === 'Committed' && r.observation_count ? r.assigned / r.observation_count : null;
    case 'cost': return r.cost || null;
    case 'uploaded': return r.created_at;
  }
};

function BatchTable({ rows, onOpenBatch, capped }: { rows: BatchListRow[]; onOpenBatch: (id: number) => void; capped: boolean }) {
  const { sorted, sort, toggle } = useSorted<BatchListRow, BatchSort>(rows, sortValue, { key: 'uploaded', desc: true });
  const th = (k: BatchSort, label: string, numeric = false) => (
    <SortTh k={k} sort={sort} onSort={toggle} numeric={numeric} className={numeric ? 'r' : ''}>{label}</SortTh>
  );
  return (
    <>
      <div className="li-tablewrap">
        <table className="li-table">
          <thead>
            <tr>
              {th('file', 'File')}{th('source', 'Source')}{th('batch_date', 'Batch date', true)}{th('status', 'Status')}
              {th('leads', 'Leads', true)}{th('new', 'New', true)}{th('assigned', 'Assigned', true)}{th('cost', 'Cost', true)}{th('uploaded', 'Uploaded', true)}
            </tr>
          </thead>
          <tbody>{sorted.map((r) => <BatchRow key={r.id} r={r} onOpen={() => onOpenBatch(r.id)} />)}</tbody>
        </table>
      </div>
      {capped && <p className="li-muted li-tablenote">Showing the newest {BATCH_LIMIT} imports. Filter by status or source to see older ones.</p>}
    </>
  );
}

function BatchRow({ r, onOpen }: { r: BatchListRow; onOpen: () => void }) {
  const committed = r.status === 'Committed';
  const phase = phaseText(r);
  return (
    <tr className="is-clickable" onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }} tabIndex={0}>
      <td className="li-ellipsis" title={r.file_name ?? ''}><b>{r.file_name}</b></td>
      <td>{r.source ?? '-'}{r.sub_lead_source && <span className="li-subtag">{r.sub_lead_source}</span>}</td>
      <td className="num">{fmtDay(r.batch_date)}</td>
      <td>
        <StatusPill status={r.status} />
        {phase && <div className="li-row-phase">{phase}</div>}
        {r.held_count > 0 && <div className="li-row-phase">{fmtInt(r.held_count)} need review</div>}
      </td>
      <td className="r num strong">{committed ? fmtInt(r.observation_count) : fmtInt(r.preview_observations)}</td>
      <td className="r num">{committed ? fmtInt(r.new_identity_count) : '-'}</td>
      <td className="r num" title={committed ? `${fmtInt(r.assigned)} of ${fmtInt(r.observation_count)} leads assigned` : undefined}>
        {committed ? (r.observation_count ? `${Math.round((r.assigned / r.observation_count) * 100)}%` : '-') : '-'}
      </td>
      <td className="r num">{r.cost ? fmtMoney(r.cost) : '-'}</td>
      <td className="r num" title={r.created_by ? `${fmtWhen(r.created_at)} by ${r.created_by}` : fmtWhen(r.created_at)}>{fmtDate(r.created_at)}</td>
    </tr>
  );
}

/**
 * Start a new import from a local file or a Google Sheet.
 *
 * The drop zone only reacts to drag and drop. The file chooser opens from
 * the "Upload a file" button alone, and the Google Sheets dialog is rendered
 * outside the drop zone: React events bubble through portals, so a dialog
 * nested in a clickable area used to open the native file chooser [F13].
 */
function StartImport({ onStarted }: { onStarted: (id: number) => void }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** A Google Sheet Drive would not export; its next upload keeps the link. */
  const [manualFor, setManualFor] = useState<DriveSpreadsheetMeta | null>(null);
  const [driveError, setDriveError] = useState<{ title: string; text: string } | null>(null);

  const upload = useMutation({
    mutationFn: ({ file, source }: { file: File; source?: SourceLocation }) => uploadOriginal(file, setStage, source),
    onSuccess: (r) => {
      setManualFor(null);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      onStarted(r.batch);
    },
    onError: (e) => toast.error((e as Error).message),
    onSettled: () => setStage(null),
  });
  const busy = upload.isPending || stage != null;

  const locationOf = (meta: DriveSpreadsheetMeta, exportedAs: string, method: 'drive_api' | 'manual_download'): SourceLocation => ({
    provider: 'google_drive', file_id: meta.id, name: meta.name, mime_type: meta.mimeType, web_view_link: meta.webViewLink,
    modified_time: meta.modifiedTime, version: meta.version, head_revision_id: meta.headRevisionId,
    owner: meta.owners?.[0]?.emailAddress ?? meta.owners?.[0]?.displayName, exported_as: exportedAs, capture_method: method,
  });
  const pick = (files: FileList | null) => {
    const f = files?.[0];
    if (!f || busy) return;
    const problem = uploadProblem(f);
    if (problem) {
      setDriveError({ title: `Could not upload ${f.name}`, text: problem });
      return;
    }
    setDriveError(null);
    upload.mutate({ file: f, source: manualFor ? locationOf(manualFor, f.type || 'application/octet-stream', 'manual_download') : undefined });
  };
  const fromDrive = async (meta: DriveSpreadsheetMeta, token: string) => {
    setPickerOpen(false);
    setDriveError(null);
    setStage('Downloading from Google Sheets');
    try {
      const { file, exportedAs } = await downloadDriveSpreadsheet(meta, token);
      upload.mutate({ file, source: locationOf(meta, exportedAs, 'drive_api') });
    } catch (e) {
      setStage(null);
      if (e instanceof GoogleDriveError && e.code === 'too_large') {
        setManualFor(meta);
      } else if (e instanceof GoogleDriveError && (e.code === 'access' || e.code === 'auth' || e.code === 'not_found')) {
        setDriveError({ title: `Could not download ${meta.name}`, text: `${e.message} Open the sheet in Google to check who has access, or ask the owner to share it with your Google account.` });
      } else {
        setDriveError({ title: `Could not download ${meta.name}`, text: (e as Error).message });
      }
    }
  };

  return (
    <section
      className={`li-start${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
      onDragOver={(e) => { if (!busy) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!pickerOpen) pick(e.dataTransfer.files); }}
      aria-label="Start a new import"
    >
      <input ref={input} type="file" hidden accept={UPLOAD_ACCEPT} onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      {busy ? (
        <div className="li-start__busy" aria-live="polite"><Spinner size={22} /><span>{stage ?? 'Working'}…</span></div>
      ) : manualFor ? (
        <div className="li-start__manual" role="alert">
          <h2>{manualFor.name} is too large for Google to export</h2>
          <ol>
            <li>Open the sheet in Google Sheets{manualFor.webViewLink && <> (<a href={manualFor.webViewLink} target="_blank" rel="noreferrer">open it</a>)</>}.</li>
            <li>Choose File, Download, Microsoft Excel (.xlsx).</li>
            <li>Upload the downloaded file here. The import keeps the link to the original sheet.</li>
          </ol>
          <div className="li-row">
            <button type="button" className="li-btn li-btn--primary" onClick={() => input.current?.click()}>Upload the downloaded file</button>
            <button type="button" className="li-btn li-btn--quiet" onClick={() => setManualFor(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="li-start__bar">
            <p className="li-start__text"><b>New import.</b> Drop a CSV or Excel file up to {MAX_UPLOAD_MB} MB here, or</p>
            <div className="li-start__actions">
              <button type="button" className="li-btn li-btn--primary" onClick={() => input.current?.click()}>Upload a file</button>
              <button type="button" className="li-btn li-btn--secondary" onClick={() => { setDriveError(null); setPickerOpen(true); }}>
                Google Sheet
              </button>
            </div>
          </div>
          {driveError && (
            <div className="li-banner li-banner--danger li-start__error" role="alert">
              <div className="li-banner__text"><b>{driveError.title}.</b> {driveError.text}</div>
              <button type="button" className="li-btn li-btn--quiet" onClick={() => setDriveError(null)}>Dismiss</button>
            </div>
          )}
        </>
      )}
      {/* Rendered last and never inside a clickable wrapper; see the comment above. */}
      <DriveSheetPicker isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onPick={fromDrive} />
    </section>
  );
}

/* Used by navigationPrefetch to warm the Imports route. */
export { fetchBatches, fetchLeadSources };
